import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BlockNoteEditorComponent } from '@/components/atoms/Editors/BlockNoteEditor/BlockNoteEditor';
import { getGuidelinesEditorOverrides } from '@/components/atoms/Editors/BlockNoteEditor/config';
import { Separator } from '@/components/ui/separator';
import useBrains from '@/hooks/useBrains';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import type { Dialog } from '@/shared/types/dialog';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { GuidelineEditorHeader } from './GuidelineEditorHeader';
import { objectionsToMarkdown } from '../../helper';

type ObjectionsGuidelineEditorProps = {
  onEditorReady?: (editor: { getMarkdown: () => Promise<string> }) => void;
  onChange?: () => void;
  enabledTools?: string[];
  dialogs?: Dialog[];
  onNavigateToLocalTools?: () => void;
};

export const ObjectionsGuidelineEditor = ({
  onEditorReady,
  onChange,
  enabledTools,
  dialogs,
  onNavigateToLocalTools,
}: ObjectionsGuidelineEditorProps = {}) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const brainId = useSelector(selectBrainId);
  const { brain, getStatus } = useBrains(brainId);
  const { contextVariables } = useBrainVariables();

  const saveObjections = useCallback(
    (_markdownToSave: string) => {
      if (!canWrite) {
        return;
      }
    },
    [canWrite]
  );

  const handleEditorSave = useCallback(
    (_markdown: string) => {
      if (!canWrite) {
        return;
      }
    },
    [canWrite]
  );

  const isObjectionsArrayNonEmpty = brain?.guidelines?.objections?.length > 0;

  // Convert objections to their new format
  useEffect(() => {
    if (!canWrite) {
      return;
    }

    if (isObjectionsArrayNonEmpty) {
      const markdownToSave = objectionsToMarkdown(brain?.guidelines?.objections);

      saveObjections(markdownToSave);
    }
  }, [brain?.guidelines, brainId, isObjectionsArrayNonEmpty, canWrite, saveObjections]);

  if (getStatus === 'success') {
    return (
      <>
        <GuidelineEditorHeader title={t('ai_agents.knowledge.objections.title')} />

        <BlockNoteEditorComponent
          onSave={handleEditorSave}
          initialMarkdown={objectionsToMarkdown(brain?.guidelines?.objections)}
          dictionary={getGuidelinesEditorOverrides().dictionary}
          editable={canWrite}
          onEditorReady={onEditorReady}
          onChange={onChange}
          enabledTools={enabledTools}
          dialogs={dialogs}
          onNavigateToLocalTools={onNavigateToLocalTools}
          contextVariableOptions={contextVariables}
        />

        <Separator decorative className="my-6 text-border" />
      </>
    );
  }

  return null;
};
